import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {
  ChangeDetectionStrategy,
  Component,
  AfterContentInit,
  ChangeDetectorRef,
  Injector,
  forwardRef, 
  input,
  computed} from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl, ControlValueAccessor, Validator, AbstractControl, ValidationErrors, FormControl, ReactiveFormsModule, NG_VALIDATORS } from '@angular/forms';
import { BaseControlValueAccessor } from '../base-control-value-accessor';
import { CommonModule, NgClass } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { IconComponent} from '@program-suite/util-icons';


type InputType = 'number' | 'text' | 'password' | 'money' | 'phone';

@Component({
  selector: 'lib-form-text',
  templateUrl: './form-text.component.html',
  styleUrls: ['./form-text.component.scss', '../form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    NgxMaskDirective,
    IconComponent,
    NgClass
  ],
  providers: [
    provideNgxMask(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormTextComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FormTextComponent),
      multi: true
    },
  ],
})
export class FormTextComponent extends BaseControlValueAccessor<string> implements AfterContentInit, ControlValueAccessor, Validator {
  hide = true;
  label = input<string>('');
  type = input<InputType>('text');
  mask = input<string | null>(null);
  startHint = input<string>();
  endHint = input<string>();
  suffix = input<string>();
  prefix = input<string>();
  additionalClass = input<string>();
  pre = input<string>('');
  step = input<string>();
  min = input<string>();
  max = input<string>();
  autocomplete = input<string>();
  specialCharaters = input<boolean>(false);
  minError = input<string>();
  maxError = input<string>();
  pattern = input<string | RegExp>('');
  patternErrorMessage = input<string>();
  readonly = input<boolean>(false);
  control!: FormControl;
  alignment = input<'left' | 'center' | 'right'>('left');
  formType = input<'search-input' | 'text-input' >('text-input'); 
  
  placeholder = input<string>('');

  // add computed signals here if needed
  prefixComputed = computed<string | undefined>(() => { return this.prefix() ? this.prefix() : this.type() == 'money' ? '$' : undefined; });
  stepComputed = computed<string | undefined>(() => { return this.step() ? this.step() : this.type() == 'money' ? '0.01' : undefined; });
  minComputed = computed<string | undefined>(() => { return this.min() ? this.min() : this.type() == 'money' ? '0' : undefined; });
  patternComputed = computed<string | RegExp>(() => {
    const type = this.type();
    const basePattern = this.pattern();
  
    if (basePattern) return basePattern;
    
    switch (type) {
      case 'password':
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W\s]).{8,}$/;
      case 'phone':
        return /^\(\d{3}\) \d{3}-\d{4}$/;
      default:
        return '';
    }
  });
  patternErrorMessageComputed = computed<string | undefined>(() => { return this.patternErrorMessage() ? this.patternErrorMessage() 
    : this.type() == 'money' ? 'Cannot be more than two decimal places' 
    : this.type() == 'password' ? 'Must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, 1 special character, and at least 8 characters long.' 
    : this.type() === 'phone'? 'Must be a valid phone number like (123) 456-7890'
    : ''; 
  });

  // TODO: conditional masks based on type
   maskComputed = computed<string | null>(() => { return this.mask() ? this.mask() 
    : this.type() == 'phone' ? '(999) 999-9999' 
    : this.type() == 'money' ? "separator.2"
    : null;
  });


  constructor(private injector: Injector, private cdRef: ChangeDetectorRef) {
    super();
  }

  ngAfterContentInit(): void {
    const ngControl: NgControl = this.injector.get(NgControl, null) as NgControl;
    if (ngControl) {
      this.control = ngControl.control as FormControl;
    } else {
      this.control = new FormControl();
    }

  }

  // OnPush component: keep `this.value` in sync so the masked-input branch's `[value]="value"` binding
  // repaints on a programmatic patchValue/setValue. The non-masked branch has no `[value]` binding —
  // it relies on Angular's DefaultValueAccessor, which writes to the DOM directly (see the template).
  override writeValue(value: string | null): void {
    this.value = (value ?? '') as string;
    this.cdRef.markForCheck();
  }

  hasPatternValidator(): boolean {
    const validator = this.control?.validator?.({} as AbstractControl);
    return !!validator?.['pattern'];
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return control.valid ? null : control.errors;
  }

  get inputType(): string {

    switch (this.type()) {
      case 'number':
        return 'number';
      case 'text':
        return 'text';
      case 'password':
        return 'password';
      case 'money':
        return 'text';
      default:
        return 'text';
    }
  }


  public get invalid(): boolean | null {
    return this.control ? this.control?.invalid : false;
  }

  public get showError(): boolean | null {
    if (!this.control) {
      return false;
    }

    const { dirty, touched } = this.control;

    return this.invalid ? dirty || touched : false;
  }

  showPasswordHandler() {
    this.hide = !this.hide;
    this.cdRef.markForCheck(); 
  }
}
